// <define:import.meta>
var define_import_meta_default = {};

// src/store.ts
import {
  setPath as setPath2,
  getPath as getPath2,
  stringToPathArray as stringToPathArray2,
  mergePathStrings
} from "@rvf/set-get";
import { create } from "zustand/react";

// src/immer.ts
import { Immer } from "immer";
var immerInstance = new Immer({
  autoFreeze: false
});
var immerImpl = (initializer) => (set, get, store) => {
  store.setState = (updater, replace2, ...a) => {
    const nextState = typeof updater === "function" ? immerInstance.produce(updater) : updater;
    return set(nextState, replace2, ...a);
  };
  return initializer(store.setState, get, store);
};
var immer = immerImpl;

// src/getters.ts
import { getPath, pathArrayToString, stringToPathArray } from "@rvf/set-get";
var getFieldValue = (state, fieldName) => getPath(state.values, fieldName);
var getFieldDefaultValue = (state, fieldName) => {
  const path = stringToPathArray(fieldName);
  const postfix = [];
  while (path.length > 0) {
    let current = pathArrayToString(path);
    if (current in state.defaultValueOverrides) {
      const parent = state.defaultValueOverrides[current];
      if (postfix.length)
        return getPath(parent, postfix);
      return parent;
    }
    postfix.push(path.pop());
  }
  return getPath(state.defaultValues, fieldName);
};
var getFieldTouched = (state, fieldName) => state.touchedFields[fieldName] ?? false;
var getFieldDirty = (state, fieldName) => state.dirtyFields[fieldName] ?? false;
var getFieldError = (state, fieldName) => {
  return state.validationErrors[fieldName] ?? null;
};
var getFieldArrayKeys = (state, fieldName) => state.fieldArrayKeys[fieldName];
var getArrayUpdateKey = (state, fieldName) => state.arrayUpdateKeys[fieldName];
var getAllTouched = (state) => state.touchedFields;
var getAllDirty = (state) => state.dirtyFields;
var getAllErrors = (state) => {
  if (state.submitStatus !== "idle")
    return state.validationErrors;
  const fieldsWithErrors = Object.entries(state.validationErrors).filter(
    ([fieldName]) => state.touchedFields[fieldName] || state.validationBehaviorConfig.initial === "onChange"
  );
  return Object.fromEntries(fieldsWithErrors);
};
var getFormId = (state) => state.formProps.id ?? state.defaultFormId;
var getFormIdOption = (state) => state.formProps.id;
var getFormProps = (state) => ({
  ...state.formProps,
  id: getFormId(state)
});
var getFormAction = (state) => state.formProps.action;

// src/dom/getCheckboxChecked.ts
var getCheckboxChecked = (checkboxValue = "on", newValue) => {
  if (Array.isArray(newValue))
    return newValue.some((val) => val === true || val === checkboxValue);
  if (typeof newValue === "boolean")
    return newValue;
  if (typeof newValue === "string")
    return newValue === checkboxValue;
  return void 0;
};
var getNextCheckboxValue = ({
  derivedValue,
  valueProp,
  currentValue
}) => {
  if (typeof derivedValue !== "boolean")
    return derivedValue;
  if (Array.isArray(currentValue)) {
    const values = new Set(currentValue);
    if (derivedValue)
      values.add(valueProp);
    else
      values.delete(valueProp);
    return [...values];
  }
  return derivedValue;
};

// src/dom/dom.ts
var setFormControlValue = (element, value) => {
  if (element instanceof HTMLInputElement) {
    switch (element.type) {
      case "checkbox":
        element.checked = Array.isArray(value) ? value?.includes(element.value) : Boolean(value);
        break;
      case "radio":
        element.checked = value === element.value;
        break;
      case "number":
        if (value == null || value === "")
          element.value = "";
        else
          element.valueAsNumber = Number(value);
        break;
      case "file":
        if (!value)
          element.value = "";
        break;
      default:
        element.value = value ? String(value) : "";
        break;
    }
  }
  if (element instanceof HTMLSelectElement) {
    for (const option of element.options) {
      option.selected = Array.isArray(value) ? value.includes(option.value) : value === option.value;
    }
  }
  if (element instanceof HTMLTextAreaElement) {
    element.value = String(value);
  }
};
var getFormControlValue = (element) => {
  if (element instanceof HTMLInputElement) {
    const input = element;
    switch (input.type) {
      case "checkbox":
      case "radio":
        return input.checked;
      case "number":
        if (input.value === "")
          return null;
        return input.valueAsNumber;
      case "file":
        if (input.multiple)
          return Array.from(input.files ?? []);
        return input.files?.[0] ?? null;
      default:
        return input.value;
    }
  }
  if (element instanceof HTMLSelectElement) {
    if (element.multiple)
      return Array.from(element.selectedOptions).map((option) => option.value);
    return element.value;
  }
  if (element instanceof HTMLTextAreaElement) {
    return element.value;
  }
  return void 0;
};
var isFormControl = (el) => el instanceof HTMLInputElement || el instanceof HTMLSelectElement || el instanceof HTMLTextAreaElement;
var sortByPosition = (elements) => {
  return [...elements].sort((a, b) => {
    const comparison = a.compareDocumentPosition(b);
    if (comparison & Node.DOCUMENT_POSITION_FOLLOWING) {
      return -1;
    } else if (comparison & Node.DOCUMENT_POSITION_PRECEDING) {
      return 1;
    }
    return 0;
  });
};
var handleRadioFocus = (sortedElements) => {
  const formControls = sortedElements.filter(isFormControl);
  const firstControl = formControls[0];
  const endOfFirstFieldsElements = formControls.findIndex(
    (el) => el.name !== firstControl.name
  );
  const firstFieldElements = formControls.slice(0, endOfFirstFieldsElements);
  if (firstFieldElements.every((el) => el.type === "radio")) {
    const checkedElement = firstFieldElements.find(
      (el) => el instanceof HTMLInputElement && el.checked
    );
    return checkedElement;
  }
};
var getElementToFocus = (elements) => {
  const sorted = sortByPosition(elements).filter((el) => "focus" in el);
  if (sorted.length === 0)
    return void 0;
  const firstElement = sorted[0];
  if (!isFormControl(firstElement))
    return firstElement;
  return handleRadioFocus(sorted) ?? firstElement;
};
var focusOrReport = (elements) => {
  const firstFocusable = getElementToFocus(elements);
  if (firstFocusable && "checkValidity" in firstFocusable && typeof firstFocusable.checkValidity === "function" && !firstFocusable.checkValidity() && "reportValidity" in firstFocusable && typeof firstFocusable.reportValidity === "function") {
    firstFocusable.reportValidity();
  } else {
    firstFocusable?.focus();
  }
};
var focusFirst = (elements) => {
  const firstFocusable = sortByPosition(elements).find(
    (element) => "focus" in element
  );
  firstFocusable?.focus();
};
var getElementsWithNames = (names, formElement) => {
  if (names.length === 0)
    return [];
  const els = document.querySelectorAll(
    names.map((name) => `[name="${name}"]`).join(",")
  );
  return [...els].filter(
    (el) => isFormControl(el) && el.form === formElement
  );
};
var getNextNativeValue = ({
  element,
  currentValue
}) => {
  const derivedValue = getFormControlValue(element);
  if (element.type === "checkbox") {
    const nextValue = getNextCheckboxValue({
      currentValue,
      derivedValue,
      valueProp: element.value
    });
    return nextValue;
  }
  if (element.type === "radio") {
    return element.value;
  }
  if (element.type === "number") {
    if (derivedValue == null || derivedValue === "")
      return null;
    if (typeof currentValue === "string")
      return String(derivedValue);
    return Number(derivedValue);
  }
  return derivedValue;
};
var onNativeChange = (event, store) => {
  if (event.defaultPrevented)
    return;
  const transientState = () => store.store.getState();
  const changed = event.target;
  const formEl = store.formRef.current;
  if (!formEl || !changed || !isFormControl(changed) || !changed.form || changed.form !== formEl)
    return;
  const name = changed.name;
  if (store.transientFieldRefs.has(name) || store.controlledFieldRefs.has(name))
    return;
  transientState().onFieldChange(
    name,
    getNextNativeValue({
      element: changed,
      currentValue: getFieldValue(transientState(), name)
    })
  );
};
var onNativeBlur = (event, store) => {
  if (event.defaultPrevented)
    return;
  const transientState = () => store.store.getState();
  const changed = event.target;
  const formEl = store.formRef.current;
  if (!formEl || !changed || !isFormControl(changed) || !changed.form || changed.form !== formEl)
    return;
  const name = changed.name;
  if (store.transientFieldRefs.has(name) || store.controlledFieldRefs.has(name))
    return;
  transientState().onFieldBlur(name);
};
var registerFormElementEvents = (store) => {
  const onChange = (event) => onNativeChange(event, store);
  const onBlur = (event) => onNativeBlur(event, store);
  document.addEventListener("input", onChange);
  document.addEventListener("focusout", onBlur);
  return () => {
    document.removeEventListener("input", onChange);
    document.removeEventListener("focusout", onBlur);
  };
};

// src/native-form-data/flatten.ts
import { setPath } from "@rvf/set-get";

// src/native-form-data/MultiValueMap.ts
import { useCallback, useRef } from "react";
var MultiValueMap = class {
  dict = /* @__PURE__ */ new Map();
  add = (key, value) => {
    if (this.dict.has(key)) {
      this.dict.get(key).push(value);
    } else {
      this.dict.set(key, [value]);
    }
  };
  delete = (key) => {
    this.dict.delete(key);
  };
  remove = (key, value) => {
    if (!this.dict.has(key))
      return;
    const array = this.dict.get(key);
    const index = array.indexOf(value);
    if (index !== -1)
      array.splice(index, 1);
    if (array.length === 0)
      this.dict.delete(key);
  };
  getAll = (key) => {
    return this.dict.get(key) ?? [];
  };
  entries = () => this.dict.entries();
  keys = () => this.dict.keys();
  values = () => this.dict.values();
  has = (key) => this.dict.has(key);
};

// src/native-form-data/flatten.ts
var objectFromPathEntries = (entries) => {
  const map = new MultiValueMap();
  entries.forEach(([key, value]) => map.add(key, value));
  return [...map.entries()].reduce(
    (acc, [key, value]) => setPath(acc, key, value.length === 1 ? value[0] : value),
    {}
  );
};
var preprocessFormData = (data) => {
  if ("entries" in data && typeof data.entries === "function")
    return objectFromPathEntries([...data.entries()]);
  if (Object.keys(data).some((key) => /[\[\]\.]/.test(key)))
    return objectFromPathEntries(Object.entries(data));
  return data;
};

// src/arrayUtil.ts
var toSwapped = (array, indexA, indexB) => {
  const itemA = array[indexA];
  const itemB = array[indexB];
  const hasItemA = indexA in array;
  const hasItemB = indexB in array;
  const updatedArray = [...array];
  if (hasItemA) {
    updatedArray[indexB] = itemA;
  } else {
    delete updatedArray[indexB];
  }
  if (hasItemB) {
    updatedArray[indexA] = itemB;
  } else {
    delete updatedArray[indexA];
  }
  return updatedArray;
};
function sparseSplice(array, start, deleteCount, item) {
  if (array.length < start && item) {
    array.length = start;
  }
  if (arguments.length === 4)
    return array.splice(start, deleteCount, item);
  else if (arguments.length === 3)
    return array.splice(start, deleteCount);
  return array.splice(start);
}
var move = (array, from, to) => {
  const [item] = sparseSplice(array, from, 1);
  sparseSplice(array, to, 0, item);
};
var insert = (array, index, value) => {
  sparseSplice(array, index, 0, value);
};
var remove = (array, index) => {
  sparseSplice(array, index, 1);
};
var replace = (array, index, value) => {
  sparseSplice(array, index, 1, value);
};

// src/store.ts
var createRefStore = () => {
  const elementRefs = new MultiValueMap();
  const symbolMaps = /* @__PURE__ */ new Map();
  return {
    has: (name) => elementRefs.has(name),
    getRefs: (fieldName) => elementRefs.getAll(fieldName),
    removeRef: (fieldName, symbol) => {
      if (symbol) {
        const el = symbolMaps.get(symbol);
        symbolMaps.delete(symbol);
        if (!el)
          throw new Error("Ref symbol not found. This is likely a bug in RVF");
        elementRefs.remove(fieldName, el);
        return;
      }
      elementRefs.delete(fieldName);
    },
    setRef: (fieldName, ref, symbol) => {
      if (symbol)
        symbolMaps.set(symbol, ref);
      elementRefs.add(fieldName, ref);
    },
    forEach: (callback) => [...elementRefs.entries()].forEach(
      ([fieldName, refs]) => refs.forEach((ref) => callback(fieldName, ref))
    ),
    all: () => [...elementRefs.entries()],
    names: () => [...elementRefs.keys()]
  };
};
var withResolvers = () => {
  let resolve;
  let reject;
  const promise = new Promise((_resolve, _reject) => {
    resolve = _resolve;
    reject = _reject;
  });
  return {
    promise,
    resolve,
    reject
  };
};
var createResolverQueue = () => {
  const resolvers = /* @__PURE__ */ new Set();
  const queueResolver = () => {
    const { promise, resolve } = withResolvers();
    resolvers.add(resolve);
    return promise;
  };
  return {
    queue: queueResolver,
    flush: () => {
      resolvers.forEach((resolve) => resolve());
      resolvers.clear();
    },
    /**
     * Waits for any pending updates to resolve if there are any.
     * Does not queue any resolvers if there are none.
     */
    await: () => {
      const res = resolvers.size > 0 ? queueResolver() : Promise.resolve();
      return res;
    }
  };
};
var CancelSubmitError = class extends Error {
};
var defaultValidationBehaviorConfig = {
  initial: "onBlur",
  whenTouched: "onChange",
  whenSubmitted: "onChange"
};
var genKey = () => `${Math.round(Math.random() * 1e4)}-${Date.now()}`;
var setPathIfUndefined = (obj, path, value) => {
  const val = getPath2(obj, path);
  if (val === void 0)
    setPath2(obj, path, value);
};
var renameFlatFieldStateKeys = (obj, path, updater) => {
  const newObj = {};
  const removeKeys = [];
  for (const [key, value] of Object.entries(obj)) {
    if (key.startsWith(path)) {
      newObj[updater(key)] = value;
      removeKeys.push(key);
    }
  }
  removeKeys.forEach((key) => delete obj[key]);
  Object.assign(obj, newObj);
};
var deleteFieldsWithPrefix = (prefixObjects, path) => {
  for (const obj of prefixObjects) {
    for (const [key, value] of Object.entries(obj)) {
      if (key.startsWith(path)) {
        delete obj[key];
      }
    }
  }
};
var moveFieldArrayKeys = (objs, fieldName, updater) => {
  objs.forEach((obj) => {
    renameFlatFieldStateKeys(obj, fieldName, (key) => {
      if (key === fieldName)
        return key;
      const suffix = key.replace(`${fieldName}`, "");
      const parts = stringToPathArray2(suffix);
      const index = Number(parts.shift());
      if (Number.isNaN(index))
        throw new Error(`Attempted to update a non-array field, ${fieldName}`);
      const newIndex = updater(index);
      return mergePathStrings(fieldName, newIndex, ...parts);
    });
  });
};
var toArrayBehavior = (config) => {
  return {
    initial: config?.initial === "onBlur" ? "onSubmit" : config?.initial ?? "onSubmit",
    whenSubmitted: config?.whenSubmitted === "onBlur" ? "onSubmit" : config?.whenSubmitted ?? "onChange"
  };
};
var createFormStateStore = ({
  defaultValues,
  controlledFieldRefs,
  transientFieldRefs,
  fieldSerializerRefs,
  resolvers,
  mutableImplStore,
  formRef,
  submitSource,
  validationBehaviorConfig = defaultValidationBehaviorConfig,
  formProps,
  flags,
  serverValidationErrors = {},
  defaultFormId
}) => create()(
  immer((set, get) => ({
    /////// State
    values: defaultValues,
    defaultValues,
    defaultValueOverrides: {},
    touchedFields: {},
    dirtyFields: {},
    validationErrors: serverValidationErrors,
    // If we have default errors, lets treat that as a failed server-side validation
    submitStatus: Object.keys(serverValidationErrors).length > 0 ? "error" : "idle",
    fieldArrayKeys: {},
    arrayUpdateKeys: {},
    validationBehaviorConfig,
    submitSource,
    formProps: {
      ...formProps,
      id: formProps.id
    },
    defaultFormId,
    flags,
    /////// Validation
    getFormValuesForValidation: ({ injectedData } = {}) => {
      if (get().submitSource === "state")
        return [{ ...get().values, ...injectedData }];
      const form = formRef.current;
      if (!form)
        throw new Error(
          "`submitSource` is set to `dom`, but no form is registered with RVF."
        );
      const formData = new FormData(form);
      if (injectedData) {
        Object.entries(injectedData).filter(([key, value]) => !!key && !!value).forEach(([key, value]) => {
          formData.set(key, value);
        });
      }
      const preprocessed = preprocessFormData(formData);
      return [preprocessed, formData];
    },
    shouldValidate: (eventType, fieldName, behaviorOverride) => {
      if (eventType === "onSubmit")
        return true;
      const config = behaviorOverride ?? get().validationBehaviorConfig;
      const currentValidationBehavior = get().submitStatus !== "idle" ? config.whenSubmitted : get().touchedFields[fieldName] ? config.whenTouched : config.initial;
      if (eventType === "onBlur")
        return currentValidationBehavior === "onBlur" || currentValidationBehavior === "onChange";
      return currentValidationBehavior === "onChange";
    },
    shouldValidateArray: (eventType, behaviorOverride) => {
      if (eventType === "onSubmit")
        return true;
      const config = behaviorOverride ?? toArrayBehavior(get().validationBehaviorConfig);
      const currentValidationBehavior = get().submitStatus !== "idle" ? config.whenSubmitted : config.initial;
      return currentValidationBehavior === "onChange";
    },
    maybeValidateArrayOperation: async (fieldName, behaviorOverride) => {
      if (get().submitSource === "dom") {
        await resolvers.queue();
      }
      if (get().shouldValidateArray("onChange", behaviorOverride)) {
        get().validateField(fieldName);
      } else {
        get().setError(fieldName, null);
      }
    },
    validate: async (nextValues, shouldMarkSubmitted = false) => {
      if (get().submitSource === "dom") {
        await resolvers.await();
      }
      const result = await mutableImplStore.validator.validate(
        nextValues ?? get().getFormValuesForValidation()[0]
      );
      if (result.data) {
        set((state) => {
          state.validationErrors = {};
        });
        return { data: result.data, errors: void 0 };
      }
      const errors = result.error?.fieldErrors ?? {};
      set((state) => {
        state.validationErrors = errors;
        if (shouldMarkSubmitted)
          state.submitStatus = "error";
      });
      return { errors, data: void 0 };
    },
    validateField: async (fieldName) => {
      const serializedData = {};
      if (get().submitSource === "dom") {
        const state = get();
        const controlledFieldNames = new Set(controlledFieldRefs.names());
        fieldSerializerRefs.all().forEach(([fieldName2, serializers]) => {
          if (serializers.length > 1) {
            console.error(
              "RVF: Multiple serializers for a single field are not supported"
            );
          }
          const serializer = serializers[0];
          serializedData[fieldName2] = serializer(
            getFieldValue(state, fieldName2)
          );
          controlledFieldNames.delete(fieldName2);
        });
        await resolvers.await();
      }
      const [values] = get().getFormValuesForValidation({
        injectedData: serializedData
      });
      const validationResult = await mutableImplStore.validator.validate(values);
      if (validationResult.data) {
        if (Object.keys(get().validationErrors).length > 0) {
          set((state) => {
            state.validationErrors = {};
          });
        }
        return { data: validationResult.data, errors: void 0 };
      }
      const fieldErrors = validationResult.error?.fieldErrors ?? {};
      const errorFields = /* @__PURE__ */ new Set();
      const incomingErrors = /* @__PURE__ */ new Set();
      const prevErrors = /* @__PURE__ */ new Set();
      Object.keys(fieldErrors).forEach((field) => {
        errorFields.add(field);
        incomingErrors.add(field);
      });
      Object.keys(get().validationErrors).forEach((field) => {
        errorFields.add(field);
        prevErrors.add(field);
      });
      const fieldsToUpdate = /* @__PURE__ */ new Set();
      const fieldsToDelete = /* @__PURE__ */ new Set();
      errorFields.forEach((field) => {
        if (!incomingErrors.has(field)) {
          fieldsToDelete.add(field);
          return;
        }
        if (prevErrors.has(field) && incomingErrors.has(field)) {
          if (fieldErrors[field] !== get().validationErrors[field])
            fieldsToUpdate.add(field);
          return;
        }
        if (fieldName === field) {
          fieldsToUpdate.add(field);
          return;
        }
        if (!prevErrors.has(field)) {
          get().shouldValidate;
          const fieldTouched = get().touchedFields[field];
          const formHasBeenSubmitted = get().submitStatus !== "idle";
          if (fieldTouched || formHasBeenSubmitted)
            fieldsToUpdate.add(field);
          return;
        }
      });
      if (fieldsToDelete.size === 0 && fieldsToUpdate.size === 0) {
        return {
          ...validationResult,
          error: { fieldErrors: get().validationErrors }
        };
      }
      set((state) => {
        fieldsToDelete.forEach((field) => {
          delete state.validationErrors[field];
        });
        fieldsToUpdate.forEach((field) => {
          state.validationErrors[field] = fieldErrors[field];
        });
      });
      return {
        ...validationResult,
        error: { fieldErrors: get().validationErrors }
      };
    },
    focusFirstInvalidField: () => {
      const {
        validationErrors,
        flags: { disableFocusOnError }
      } = get();
      if (disableFocusOnError)
        return;
      const refElementsWithErrors = Object.keys(validationErrors).flatMap((fieldName) => [
        ...transientFieldRefs.getRefs(fieldName),
        ...controlledFieldRefs.getRefs(fieldName)
      ]).filter((val) => val != null);
      if (formRef.current) {
        const unRegisteredNames = Object.keys(validationErrors).filter(
          (name) => !transientFieldRefs.has(name) && !controlledFieldRefs.has(name)
        );
        const otherErrorElements = getElementsWithNames(
          unRegisteredNames,
          formRef.current
        );
        refElementsWithErrors.push(...otherErrorElements);
      }
      focusOrReport(refElementsWithErrors);
    },
    /////// Events
    onFieldChange: (fieldName, value, validationBehaviorConfig2) => {
      set((state) => {
        setPath2(state.values, fieldName, value);
        const defaultValue = getFieldDefaultValue(state, fieldName);
        state.dirtyFields[fieldName] = value !== defaultValue;
      });
      if (get().submitSource === "dom" && controlledFieldRefs.has(fieldName) && !fieldSerializerRefs.has(fieldName)) {
        void resolvers.queue();
      }
      if (get().shouldValidate("onChange", fieldName, validationBehaviorConfig2)) {
        get().validateField(fieldName);
      } else {
        get().setError(fieldName, null);
      }
    },
    onFieldBlur: (fieldName, validationBehaviorConfig2) => {
      set((state) => {
        state.touchedFields[fieldName] = true;
      });
      if (get().shouldValidate("onBlur", fieldName, validationBehaviorConfig2)) {
        get().validateField(fieldName);
      }
    },
    onSubmit: async (submitterData, submitterOptions = {}) => {
      set((state) => {
        state.submitStatus = "submitting";
      });
      const [rawValues, formData] = get().getFormValuesForValidation({
        injectedData: submitterData
      });
      const doValidation = () => get().validate(rawValues, true);
      let result = void 0;
      const runSubmission = async (data) => {
        try {
          let response;
          if (get().submitSource === "state") {
            response = await mutableImplStore.onSubmit(data, submitterOptions);
          } else {
            if (!formData)
              throw new Error(
                "Missing form data. This is likely a bug in RVF"
              );
            response = await mutableImplStore.onSubmit(
              data,
              formData,
              submitterOptions
            );
          }
          try {
            await mutableImplStore.onSubmitSuccess?.(response);
          } finally {
            set((state) => {
              state.submitStatus = "success";
            });
          }
        } catch (err) {
          try {
            await mutableImplStore.onSubmitFailure?.(err);
          } finally {
            set((state) => {
              state.submitStatus = "error";
            });
          }
        }
      };
      let submitted = false;
      try {
        await mutableImplStore.onBeforeSubmit?.({
          unvalidatedData: rawValues,
          getValidatedData: async () => {
            result = await doValidation();
            if (result.errors) {
              throw new CancelSubmitError();
            }
            return result.data;
          },
          getFormData: () => {
            if (!formData) {
              throw new Error(
                "FormData can only be accessed when `submitSource` is set to `dom`"
              );
            }
            return formData;
          },
          cancelSubmit: () => {
            throw new CancelSubmitError();
          },
          performSubmit: async (data) => {
            submitted = true;
            await runSubmission(data);
          },
          submitterOptions
        });
      } catch (err) {
        try {
          if (!(err instanceof CancelSubmitError)) {
            console.error(err);
            await mutableImplStore.onSubmitFailure?.(err);
          }
        } finally {
          set((state) => {
            state.submitStatus = "error";
          });
        }
        return;
      }
      if (submitted)
        return;
      if (!result)
        result = await doValidation();
      if (result.errors) {
        get().focusFirstInvalidField();
        await mutableImplStore.onInvalidSubmit?.();
        return;
      }
      if (get().flags.reloadDocument) {
        const form = formRef.current;
        if (!form)
          throw new Error(
            "Can't use reloadDocument without a native form element"
          );
        if (submitSource !== "dom")
          throw new Error(
            "Can't use reloadDocument with the state submit source"
          );
        form.submit();
        return;
      }
      await runSubmission(result.data);
    },
    /////// Actions
    syncOptions: ({
      submitSource: submitSource2,
      validationBehaviorConfig: validationBehaviorConfig2 = defaultValidationBehaviorConfig,
      formProps: formProps2,
      flags: flags2
    }) => {
      set((state) => {
        state.submitSource = submitSource2;
        state.validationBehaviorConfig = validationBehaviorConfig2;
        state.formProps = {
          ...formProps2,
          id: formProps2.id ?? state.formProps.id
        };
        state.flags = flags2;
      });
    },
    syncServerValidationErrors: (errors) => {
      if (errors === get().validationErrors)
        return;
      set((state) => {
        state.validationErrors = errors;
        state.submitStatus = "error";
      });
      get().focusFirstInvalidField();
    },
    setValue: (fieldName, value) => {
      set((state) => {
        if (fieldName)
          setPath2(state.values, fieldName, value);
        else
          state.values = value;
        deleteFieldsWithPrefix([state.fieldArrayKeys], fieldName);
      });
      if (get().submitSource === "dom" && controlledFieldRefs.has(fieldName) && !fieldSerializerRefs.has(fieldName)) {
        void resolvers.queue();
      }
      transientFieldRefs.getRefs(fieldName).forEach((ref) => {
        setFormControlValue(ref, value);
      });
    },
    setAllValues: (data) => {
      set((state) => {
        state.values = data;
      });
      if (get().submitSource === "dom") {
        void resolvers.queue();
      }
    },
    setTouched: (fieldName, value) => {
      set((state) => {
        state.touchedFields[fieldName] = value;
      });
    },
    setDirty: (fieldName, value) => {
      set((state) => {
        state.dirtyFields[fieldName] = value;
      });
    },
    setError: (fieldName, value) => {
      set((state) => {
        if (value == null)
          delete state.validationErrors[fieldName];
        else
          state.validationErrors[fieldName] = value;
      });
    },
    setAllTouched: (data) => {
      set((state) => {
        state.touchedFields = data;
      });
    },
    setAllDirty: (data) => {
      set((state) => {
        state.dirtyFields = data;
      });
    },
    setAllErrors: (data) => {
      set((state) => {
        state.validationErrors = data;
      });
    },
    ///////// Other actions
    reset: (nextValues = get().defaultValues) => {
      set((state) => {
        state.values = nextValues;
        state.defaultValues = nextValues;
        state.defaultValueOverrides = {};
        state.touchedFields = {};
        state.dirtyFields = {};
        state.validationErrors = {};
        state.fieldArrayKeys = {};
        state.arrayUpdateKeys = {};
        state.submitStatus = "idle";
      });
      if (get().submitSource === "dom") {
        void resolvers.queue();
      }
      transientFieldRefs.forEach((fieldName, ref) => {
        if (!ref)
          return;
        setFormControlValue(ref, getPath2(nextValues, fieldName));
      });
    },
    resetField: (fieldName, opts = {}) => {
      const currentDefaultValue = getFieldDefaultValue(get(), fieldName);
      const shouldOverrideDefaultValue = "defaultValue" in opts;
      const nextValue = shouldOverrideDefaultValue ? opts.defaultValue : currentDefaultValue;
      set((state) => {
        setPath2(state.values, fieldName, nextValue);
        deleteFieldsWithPrefix(
          [
            state.touchedFields,
            state.validationErrors,
            state.dirtyFields,
            state.fieldArrayKeys,
            state.defaultValueOverrides
          ],
          fieldName
        );
        state.arrayUpdateKeys[fieldName] = genKey();
        if (shouldOverrideDefaultValue)
          state.defaultValueOverrides[fieldName] = opts.defaultValue;
      });
      if (get().submitSource === "dom" && controlledFieldRefs.has(fieldName) && !fieldSerializerRefs.has(fieldName)) {
        void resolvers.queue();
      }
      transientFieldRefs.forEach((fieldName2, ref) => {
        if (!ref)
          return;
        setFormControlValue(ref, nextValue);
      });
    },
    ///////// Arrays
    getFieldArrayKeys: (fieldName) => {
      const currentKeys = get().fieldArrayKeys[fieldName];
      if (currentKeys)
        return currentKeys;
      const valueInState = getPath2(get().values, fieldName);
      const value = valueInState === void 0 ? [] : valueInState;
      if (!Array.isArray(value)) {
        console.warn(
          "Tried to treat a non-array as an array. Make sure you used the correct field name and set a default value."
        );
        return [];
      }
      const newKeys = value.map(() => genKey());
      queueMicrotask(() => {
        set((state) => {
          state.fieldArrayKeys[fieldName] = newKeys;
        });
      });
      return newKeys;
    },
    arrayPush: (fieldName, value, validationBehaviorConfig2) => {
      set((state) => {
        setPathIfUndefined(state.values, fieldName, []);
        const val = getPath2(state.values, fieldName);
        if (!Array.isArray(val))
          throw new Error("Can't push to a non-array");
        const previousLength = val.length;
        val.push(value);
        state.fieldArrayKeys[fieldName]?.push(genKey());
        state.arrayUpdateKeys[fieldName] = genKey();
        state.defaultValueOverrides[`${fieldName}[${previousLength}]`] = value;
      });
      void get().maybeValidateArrayOperation(
        fieldName,
        validationBehaviorConfig2
      );
    },
    arrayPop: (fieldName, validationBehaviorConfig2) => {
      set((state) => {
        setPathIfUndefined(state.values, fieldName, []);
        const val = getPath2(state.values, fieldName);
        if (!Array.isArray(val))
          throw new Error("Can't pop from a non-array");
        const numItems = val.length;
        val.pop();
        state.fieldArrayKeys[fieldName]?.pop();
        state.arrayUpdateKeys[fieldName] = genKey();
        deleteFieldsWithPrefix(
          [
            state.touchedFields,
            state.validationErrors,
            state.dirtyFields,
            state.fieldArrayKeys,
            state.defaultValueOverrides
          ],
          `${fieldName}[${numItems - 1}]`
        );
      });
      void get().maybeValidateArrayOperation(
        fieldName,
        validationBehaviorConfig2
      );
    },
    arrayShift: (fieldName, validationBehaviorConfig2) => {
      set((state) => {
        setPathIfUndefined(state.values, fieldName, []);
        const val = getPath2(state.values, fieldName);
        if (!Array.isArray(val))
          throw new Error("Can't shift from a non-array");
        val.shift();
        state.fieldArrayKeys[fieldName]?.shift();
        state.arrayUpdateKeys[fieldName] = genKey();
        deleteFieldsWithPrefix(
          [
            state.touchedFields,
            state.validationErrors,
            state.dirtyFields,
            state.fieldArrayKeys,
            state.defaultValueOverrides
          ],
          `${fieldName}[0]`
        );
        moveFieldArrayKeys(
          [
            state.touchedFields,
            state.validationErrors,
            state.dirtyFields,
            state.fieldArrayKeys,
            state.defaultValueOverrides
          ],
          fieldName,
          (index) => index - 1
        );
      });
      void get().maybeValidateArrayOperation(
        fieldName,
        validationBehaviorConfig2
      );
    },
    arrayUnshift: (fieldName, value, validationBehaviorConfig2) => {
      set((state) => {
        setPathIfUndefined(state.values, fieldName, []);
        const val = getPath2(state.values, fieldName);
        if (!Array.isArray(val))
          throw new Error("Can't unshift to a non-array");
        val.unshift(value);
        state.fieldArrayKeys[fieldName]?.unshift(genKey());
        state.arrayUpdateKeys[fieldName] = genKey();
        moveFieldArrayKeys(
          [
            state.touchedFields,
            state.validationErrors,
            state.dirtyFields,
            state.fieldArrayKeys,
            state.defaultValueOverrides
          ],
          fieldName,
          (index) => index + 1
        );
        state.defaultValueOverrides[`${fieldName}[0]`] = value;
      });
      void get().maybeValidateArrayOperation(
        fieldName,
        validationBehaviorConfig2
      );
    },
    arrayInsert: (fieldName, insertAtIndex, value, validationBehaviorConfig2) => {
      set((state) => {
        setPathIfUndefined(state.values, fieldName, []);
        const val = getPath2(state.values, fieldName);
        if (!Array.isArray(val))
          throw new Error("Can't insert to a non-array");
        insert(val, insertAtIndex, value);
        if (state.fieldArrayKeys[fieldName])
          insert(state.fieldArrayKeys[fieldName], insertAtIndex, genKey());
        state.arrayUpdateKeys[fieldName] = genKey();
        moveFieldArrayKeys(
          [
            state.touchedFields,
            state.validationErrors,
            state.dirtyFields,
            state.fieldArrayKeys,
            state.defaultValueOverrides
          ],
          fieldName,
          (index) => index >= insertAtIndex ? index + 1 : index
        );
        state.defaultValueOverrides[`${fieldName}[${insertAtIndex}]`] = value;
      });
      void get().maybeValidateArrayOperation(
        fieldName,
        validationBehaviorConfig2
      );
    },
    arrayMove: (fieldName, fromIndex, toIndex, validationBehaviorConfig2) => {
      set((state) => {
        setPathIfUndefined(state.values, fieldName, []);
        const val = getPath2(state.values, fieldName);
        if (!Array.isArray(val))
          throw new Error("Can't move from a non-array");
        move(val, fromIndex, toIndex);
        if (state.fieldArrayKeys[fieldName])
          move(state.fieldArrayKeys[fieldName], fromIndex, toIndex);
        state.arrayUpdateKeys[fieldName] = genKey();
        moveFieldArrayKeys(
          [
            state.touchedFields,
            state.validationErrors,
            state.dirtyFields,
            state.fieldArrayKeys,
            state.defaultValueOverrides
          ],
          fieldName,
          (index) => {
            if (index === fromIndex)
              return toIndex;
            let res = index;
            if (index > fromIndex)
              res--;
            if (res >= toIndex)
              res++;
            return res;
          }
        );
      });
      void get().maybeValidateArrayOperation(
        fieldName,
        validationBehaviorConfig2
      );
    },
    arrayRemove: (fieldName, removeIndex, validationBehaviorConfig2) => {
      set((state) => {
        setPathIfUndefined(state.values, fieldName, []);
        const val = getPath2(state.values, fieldName);
        if (!Array.isArray(val))
          throw new Error("Can't remove from a non-array");
        remove(val, removeIndex);
        if (state.fieldArrayKeys[fieldName])
          remove(state.fieldArrayKeys[fieldName], removeIndex);
        state.arrayUpdateKeys[fieldName] = genKey();
        deleteFieldsWithPrefix(
          [
            state.touchedFields,
            state.validationErrors,
            state.dirtyFields,
            state.fieldArrayKeys,
            state.defaultValueOverrides
          ],
          `${fieldName}[${removeIndex}]`
        );
        moveFieldArrayKeys(
          [
            state.touchedFields,
            state.validationErrors,
            state.dirtyFields,
            state.fieldArrayKeys,
            state.defaultValueOverrides
          ],
          fieldName,
          (index) => index > removeIndex ? index - 1 : index
        );
      });
      void get().maybeValidateArrayOperation(
        fieldName,
        validationBehaviorConfig2
      );
    },
    arraySwap: (fieldName, fromIndex, toIndex, validationBehaviorConfig2) => {
      set((state) => {
        setPathIfUndefined(state.values, fieldName, []);
        const val = getPath2(state.values, fieldName);
        if (!Array.isArray(val))
          throw new Error("Can't swap from a non-array");
        setPath2(state.values, fieldName, toSwapped(val, fromIndex, toIndex));
        if (state.fieldArrayKeys[fieldName]) {
          state.fieldArrayKeys[fieldName] = toSwapped(
            state.fieldArrayKeys[fieldName],
            fromIndex,
            toIndex
          );
        }
        state.arrayUpdateKeys[fieldName] = genKey();
        moveFieldArrayKeys(
          [
            state.touchedFields,
            state.validationErrors,
            state.dirtyFields,
            state.fieldArrayKeys,
            state.defaultValueOverrides
          ],
          fieldName,
          (index) => {
            if (index === fromIndex)
              return toIndex;
            if (index === toIndex)
              return fromIndex;
            return index;
          }
        );
      });
      void get().maybeValidateArrayOperation(
        fieldName,
        validationBehaviorConfig2
      );
    },
    arrayReplace: (fieldName, index, value, validationBehaviorConfig2) => {
      set((state) => {
        setPathIfUndefined(state.values, fieldName, []);
        const val = getPath2(state.values, fieldName);
        if (!Array.isArray(val))
          throw new Error("Can't replace from a non-array");
        replace(val, index, value);
        if (state.fieldArrayKeys[fieldName])
          replace(state.fieldArrayKeys[fieldName], index, genKey());
        state.arrayUpdateKeys[fieldName] = genKey();
        deleteFieldsWithPrefix(
          [
            state.touchedFields,
            state.validationErrors,
            state.dirtyFields,
            state.fieldArrayKeys,
            state.defaultValueOverrides
          ],
          `${fieldName}[${index}]`
        );
        state.defaultValueOverrides[`${fieldName}[${index}]`] = value;
      });
      void get().maybeValidateArrayOperation(
        fieldName,
        validationBehaviorConfig2
      );
    }
  }))
);

// src/dom/getRadioChecked.ts
var getRadioChecked = (radioValue = "on", newValue) => {
  if (typeof newValue === "string")
    return newValue === radioValue;
  return void 0;
};
if (define_import_meta_default.vitest) {
  const { it, expect } = define_import_meta_default.vitest;
  it("getRadioChecked", () => {
    expect(getRadioChecked("on", "on")).toBe(true);
    expect(getRadioChecked("on", void 0)).toBe(void 0);
    expect(getRadioChecked("trueValue", void 0)).toBe(void 0);
    expect(getRadioChecked("trueValue", "bob")).toBe(false);
    expect(getRadioChecked("trueValue", "trueValue")).toBe(true);
  });
}

// src/dom/event.ts
var isEvent = (eventOrValue) => typeof eventOrValue === "object" && eventOrValue !== null && "target" in eventOrValue && eventOrValue.target instanceof HTMLElement;

// src/form.ts
import { createTrackedSelector } from "react-tracked";
import {
  mergePathStrings as mergePathStrings2
} from "@rvf/set-get";
var createFormScope = ({
  defaultValues,
  serverValidationErrors,
  validator,
  onSubmit,
  onSubmitSuccess,
  onSubmitFailure,
  onBeforeSubmit,
  onInvalidSubmit,
  validationBehaviorConfig,
  submitSource,
  formProps,
  flags,
  defaultFormId
}) => {
  const transientFieldRefs = createRefStore();
  const controlledFieldRefs = createRefStore();
  const fieldSerializerRefs = createRefStore();
  const resolvers = createResolverQueue();
  const formRef = { current: null };
  const mutableImplStore = {
    validator,
    onSubmit,
    onSubmitSuccess,
    onSubmitFailure,
    onBeforeSubmit,
    onInvalidSubmit
  };
  const store = createFormStateStore({
    defaultValues,
    serverValidationErrors,
    transientFieldRefs,
    controlledFieldRefs,
    fieldSerializerRefs,
    resolvers,
    formRef,
    submitSource,
    mutableImplStore,
    validationBehaviorConfig,
    formProps,
    flags,
    defaultFormId
  });
  const subformCache = /* @__PURE__ */ new Map();
  const rvfStore = {
    transientFieldRefs,
    controlledFieldRefs,
    fieldSerializerRefs,
    resolvers,
    formRef,
    mutableImplStore,
    store,
    subformCache,
    useStoreState: createTrackedSelector(store)
  };
  return instantiateFormScope(rvfStore, "");
};
var instantiateFormScope = (store, prefix) => ({
  __brand__: "rvf",
  __type__FormInputData: {},
  __field_prefix__: prefix,
  __store__: store,
  scope(field) {
    const newPrefix = mergePathStrings2(prefix, field);
    if (store.subformCache.has(newPrefix))
      return store.subformCache.get(newPrefix);
    const scoped = instantiateFormScope(store, newPrefix);
    store.subformCache.set(newPrefix, scoped);
    return scoped;
  }
});
var scopeFormScope = (parentForm, prefix) => {
  return parentForm.scope(prefix);
};

// src/constants.ts
var FORM_ID_FIELD_NAME = "rvfFormId";

// src/createValidator.ts
function createValidator(validator) {
  return {
    validate: async (value) => {
      const data = preprocessFormData(value);
      const result = await validator.validate(data);
      const formId = data[FORM_ID_FIELD_NAME];
      if (result.error) {
        return {
          data: void 0,
          error: {
            fieldErrors: result.error,
            formId
          },
          submittedData: data,
          formId
        };
      }
      return {
        data: result.data,
        error: void 0,
        submittedData: data,
        formId
      };
    }
  };
}
var isValidationErrorResponse = (response) => typeof response === "object" && response !== null && "fieldErrors" in response;

// src/tracking.ts
import { getUntrackedObject } from "react-tracked";
var getOriginalObject = (obj) => getUntrackedObject(obj);

// src/standardSchema.ts
import { pathArrayToString as pathArrayToString2 } from "@rvf/set-get";
var sanitizePath = (path) => {
  const sanitized = [];
  for (const segment of path) {
    const key = typeof segment === "object" && "key" in segment ? segment.key : segment;
    if (typeof key === "string" || typeof key === "number") {
      sanitized.push(key);
    } else {
      console.error(
        `RVF doesn't support path segments that aren't strings or numbers. Got ${typeof key}. Path prefix was ${pathArrayToString2(sanitized)}.`
      );
      return void 0;
    }
  }
  return sanitized;
};
var withStandardSchema = (schema) => createValidator({
  validate: async (value) => {
    const result = await schema["~standard"].validate(value);
    if (result.issues) {
      const fieldErrors = {};
      result.issues.forEach((issue) => {
        const sanitizedPath = sanitizePath(issue.path ?? []);
        if (!sanitizedPath)
          return;
        const pathString = pathArrayToString2(sanitizedPath);
        if (!fieldErrors[pathString])
          fieldErrors[pathString] = issue.message;
      });
      return { data: void 0, error: fieldErrors };
    }
    return { data: result.value, error: void 0 };
  }
});

// src/native-form-data/parseFormData.ts
var parseFormData = async (formDataOrRequest, schema) => {
  const formData = "formData" in formDataOrRequest && typeof formDataOrRequest.formData === "function" ? await formDataOrRequest.formData() : formDataOrRequest;
  return await withStandardSchema(schema).validate(formData);
};
export {
  FORM_ID_FIELD_NAME,
  createFormScope,
  createFormStateStore,
  createRefStore,
  createResolverQueue,
  createValidator,
  deleteFieldsWithPrefix,
  focusFirst,
  focusOrReport,
  getAllDirty,
  getAllErrors,
  getAllTouched,
  getArrayUpdateKey,
  getCheckboxChecked,
  getElementsWithNames,
  getFieldArrayKeys,
  getFieldDefaultValue,
  getFieldDirty,
  getFieldError,
  getFieldTouched,
  getFieldValue,
  getFormAction,
  getFormControlValue,
  getFormId,
  getFormIdOption,
  getFormProps,
  getNextCheckboxValue,
  getNextNativeValue,
  getOriginalObject,
  getRadioChecked,
  isEvent,
  isFormControl,
  isValidationErrorResponse,
  moveFieldArrayKeys,
  objectFromPathEntries,
  onNativeBlur,
  onNativeChange,
  parseFormData,
  preprocessFormData,
  registerFormElementEvents,
  renameFlatFieldStateKeys,
  scopeFormScope,
  setFormControlValue,
  sortByPosition,
  toArrayBehavior,
  withStandardSchema
};
//# sourceMappingURL=index.esm.js.map